import React from "react";
import logoMombasa from "../../../core/img/branch-logos/logo_mombasa.jpg";
import logoNairobi from "../../../core/img/branch-logos/logo_nairobi.jpg";
import logoSystems from "../../../core/img/branch-logos/logo_systems.jpg";
import logoTexastrack from "../../../core/img/branch-logos/logo_texastrack.png";
import { groupByCustomer, guardName, shortDate } from "./dutyListModel";

const LOGO_BY_BRANCH = { MOMBASA: logoMombasa, NAIROBI: logoNairobi, SYSTEMS: logoSystems, TEXASTRACK: logoTexastrack };

export const VIEW_TITLES = {
  detail: "Duty List",
  summary: "Duty List - Summary",
  guards: "Duty List - Employees",
  new: "New Assignments",
  blank: "Blank Assignments",
};

/**
 * Printable Duty List - one template for the preview modal and, via
 * renderToStaticMarkup, the print popup (which has no stylesheet, so all
 * styles are inline). Prints whichever view is open, with the page's
 * filters written under the title so a printout says what it covers.
 *
 * `limitRows` trims the Detail grid for the on-screen preview only; the
 * print itself always gets every line.
 */
const DutyListPrintPreview = ({
  view,
  data,
  rows,
  guards,
  summary,
  totals,
  guardRows,
  newRows,
  blankRows,
  blankWindowText,
  subtitle,
  branchCode,
  companyName,
  options,
  limitRows,
}) => {
  // Sizes raised 2026-09-15 (list views +2px, the 31-day grid +1px - the most
  // its day columns take on A4 landscape without clipping a 5-digit code).
  const small = view === "detail";
  const base = { fontFamily: "Arial, Helvetica, sans-serif", color: "#000" };
  const th = {
    padding: small ? "2px 1px" : "4px 6px",
    fontSize: small ? "8.5px" : "12px",
    borderBottom: "1.5px solid #000",
    background: "#eee",
    textAlign: "left",
  };
  const td = {
    padding: small ? "1px 1px" : "3px 6px",
    fontSize: small ? "8.5px" : "12px",
    borderBottom: "1px solid #ccc",
    verticalAlign: "top",
  };
  const num = { ...td, textAlign: "right" };
  const numTh = { ...th, textAlign: "right" };
  const fullBg = "#e3f1e8";
  const days = Array.from({ length: data.daysInMonth }, (_, i) => i + 1);

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
      <div>
        <div style={{ fontSize: "17px", fontWeight: 700 }}>{VIEW_TITLES[view]}</div>
        <div style={{ fontSize: "11.5px" }}>{subtitle}</div>
        {view === "blank" && <div style={{ fontSize: "11.5px" }}>{blankWindowText}</div>}
      </div>
      <div style={{ textAlign: "right" }}>
        <img src={LOGO_BY_BRANCH[branchCode] || logoMombasa} alt="" style={{ height: "40px", objectFit: "contain" }} />
        {companyName && <div style={{ fontSize: "11.5px", fontWeight: 700 }}>{companyName}</div>}
        <div style={{ fontSize: "9.5px", color: "#555" }}>Printed {new Date().toLocaleString("en-GB")}</div>
      </div>
    </div>
  );

  const detailTable = (groupList, withHead = true) => (
    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "18px" }} />
        <col style={{ width: "105px" }} />
        {days.map((d) => (
          <col key={d} />
        ))}
        <col style={{ width: "20px" }} />
      </colgroup>
      {withHead && (
        <thead>
          <tr>
            <th style={th}>#</th>
            <th style={th}>Assignment</th>
            {days.map((d) => (
              <th key={d} style={{ ...th, textAlign: "center" }}>
                {d}
              </th>
            ))}
            <th style={numTh}>Days</th>
          </tr>
        </thead>
      )}
      <tbody>
        {groupList.map((g) => (
          <React.Fragment key={g.custId}>
            <tr>
              <td colSpan={days.length + 3} style={{ ...td, fontWeight: 700, fontSize: "9.5px", background: "#f6f6f6", paddingTop: "3px" }}>
                {g.custName}
                <span style={{ fontWeight: 400, color: "#555" }}> · {[...new Set(g.rows.map((r) => r.area).filter(Boolean))].join(", ")}</span>
              </td>
            </tr>
            {g.rows.map((r) => (
              <tr key={r.key} style={{ background: r.shift === "Night" ? "#e9ecf5" : undefined, pageBreakInside: "avoid" }}>
                <td style={{ ...td, color: "#555" }}>{r.srl}</td>
                <td style={{ ...td, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  <b>{r.shift === "Night" ? "N" : "D"}</b> {r.assignmentName}
                  {r.isTemp ? " (T)" : ""}
                </td>
                {days.map((d) => {
                  const code = r.codes[d - 1];
                  const blank = code == null && r.expected[d - 1];
                  return (
                    <td
                      key={d}
                      style={{
                        ...td,
                        textAlign: "center",
                        borderLeft: "1px solid #ddd",
                        background: blank && options.shadeBlank ? "#f8d7da" : undefined,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        fontSize: "7.8px",
                      }}
                    >
                      {code != null ? code : ""}
                    </td>
                  );
                })}
                <td style={num}>{r.filledDays}</td>
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );

  let body;
  if (view === "detail") {
    // Serial numbers across the whole print, then optionally trimmed for preview.
    let n = 0;
    const groups = groupByCustomer(rows).map((g) => ({
      ...g,
      rows: g.rows.map((r) => {
        n += 1;
        return { ...r, srl: n };
      }),
    }));
    let shown = groups;
    if (limitRows) {
      let count = 0;
      shown = [];
      for (const g of groups) {
        if (count >= limitRows) break;
        shown.push(g);
        count += g.rows.length;
      }
    }
    const usedCodes = new Set();
    shown.forEach((g) => g.rows.forEach((r) => r.codes.forEach((c) => c != null && usedCodes.add(Number(c)))));

    body = (
      <>
        {options.customerPerPage
          ? shown.map((g, i) => (
              <div key={g.custId} style={i > 0 ? { pageBreakBefore: "always", breakBefore: "page" } : undefined}>
                {i > 0 && header}
                {detailTable([g])}
              </div>
            ))
          : detailTable(shown)}
        {options.guardKey && usedCodes.size > 0 && (
          <div style={{ pageBreakBefore: "always", breakBefore: "page" }}>
            <div style={{ fontSize: "13px", fontWeight: 700, margin: "4px 0 6px" }}>Guard codes</div>
            <div style={{ columnCount: 4, columnGap: "14px", fontSize: "9.5px" }}>
              {[...usedCodes]
                .sort((a, b) => a - b)
                .map((c) => (
                  <div key={c} style={{ breakInside: "avoid" }}>
                    <b>{c}</b> {guardName(guards, c)}
                  </div>
                ))}
            </div>
          </div>
        )}
      </>
    );
  } else if (view === "summary") {
    body = (
      <>
        <div style={{ fontSize: "11.5px", marginBottom: "6px" }}>
          Customers {totals.customers} · Posts {totals.daySlots + totals.nightSlots} ({totals.daySlots} Day, {totals.nightSlots} Night; {totals.tempSlots} temp) · Full month {totals.fullMonthSlots} of {totals.daySlots + totals.nightSlots} posts · Part month{" "}
          {totals.partSlots} posts ({totals.partShifts} shifts) · Shifts worked {totals.dayShifts + totals.nightShifts} of {totals.dueShifts} due ({totals.dayShifts} Day, {totals.nightShifts} Night) · Blank{" "}
          {totals.blankShifts} · Staff used {totals.guards}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th rowSpan={2} style={{ ...th, verticalAlign: "bottom" }}>#</th>
              <th rowSpan={2} style={{ ...th, verticalAlign: "bottom" }}>Customer</th>
              <th rowSpan={2} style={{ ...th, verticalAlign: "bottom", width: "13%" }}>Area</th>
              <th colSpan={2} style={{ ...th, textAlign: "center", borderBottom: "1px solid #999" }}>Posts</th>
              <th colSpan={2} style={{ ...th, textAlign: "center", borderBottom: "1px solid #999" }}>Post type</th>
              <th colSpan={2} style={{ ...th, textAlign: "center", borderBottom: "1px solid #999", background: fullBg }}>Full month</th>
              <th rowSpan={2} style={{ ...th, verticalAlign: "bottom" }}>Part month - why</th>
              <th colSpan={2} style={{ ...th, textAlign: "center", borderBottom: "1px solid #999" }}>Shifts worked</th>
              <th rowSpan={2} style={{ ...numTh, verticalAlign: "bottom" }}>Blank</th>
              <th rowSpan={2} style={{ ...numTh, verticalAlign: "bottom" }}>Staff used</th>
            </tr>
            <tr>
              <th style={numTh}>Day</th>
              <th style={numTh}>Night</th>
              <th style={numTh}>Perm</th>
              <th style={numTh}>Temp</th>
              <th style={{ ...th, textAlign: "center", background: fullBg }}>Day</th>
              <th style={{ ...th, textAlign: "center", background: fullBg }}>Night</th>
              <th style={numTh}>Day</th>
              <th style={numTh}>Night</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((s, i) => (
              <tr key={s.custId} style={{ pageBreakInside: "avoid" }}>
                <td style={td}>{i + 1}</td>
                <td style={{ ...td, fontWeight: 600 }}>{s.custName}</td>
                <td style={td}>{s.areas}</td>
                <td style={num}>{s.daySlots}</td>
                <td style={num}>{s.nightSlots}</td>
                <td style={num}>{s.permSlots}</td>
                <td style={{ ...num, fontWeight: s.tempSlots ? 700 : 400 }}>{s.tempSlots}</td>
                <td style={{ ...td, textAlign: "center", background: fullBg, fontWeight: s.fullDayText === "Yes" ? 700 : 400 }}>{s.fullDayText}</td>
                <td style={{ ...td, textAlign: "center", background: fullBg, fontWeight: s.fullNightText === "Yes" ? 700 : 400 }}>{s.fullNightText}</td>
                <td style={td}>
                  {s.partMonth.map((p) => (
                    <div key={p.full}>{p.full}</div>
                  ))}
                </td>
                <td style={num}>{s.dayShifts}</td>
                <td style={num}>{s.nightShifts}</td>
                <td style={num}>{s.blankShifts}</td>
                <td style={num}>{s.guards}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  } else if (view === "guards") {
    body = (
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>#</th>
            <th style={th}>Code</th>
            <th style={th}>Employee</th>
            <th style={numTh}>Day</th>
            <th style={numTh}>Night</th>
            <th style={numTh}>Total</th>
            <th style={th}>Customers</th>
          </tr>
        </thead>
        <tbody>
          {guardRows.map((g, i) => (
            <tr key={g.empCode} style={{ pageBreakInside: "avoid" }}>
              <td style={td}>{i + 1}</td>
              <td style={{ ...td, fontWeight: 700 }}>{g.empCode}</td>
              <td style={td}>{g.name}</td>
              <td style={num}>{g.dayShifts}</td>
              <td style={num}>{g.nightShifts}</td>
              <td style={{ ...num, fontWeight: 700 }}>{g.total}</td>
              <td style={td}>{g.customerList}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  } else {
    const list = view === "new" ? newRows : blankRows;
    body = (
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>#</th>
            <th style={th}>Customer</th>
            <th style={th}>Area</th>
            <th style={th}>Assignment</th>
            <th style={th}>Shift</th>
            {view === "new" ? (
              <>
                <th style={th}>From</th>
                <th style={th}>To</th>
                <th style={numTh}>Days worked</th>
              </>
            ) : (
              <>
                <th style={numTh}>Blank</th>
                <th style={th}>Dates</th>
                <th style={{ ...th, width: "25%" }}>Remarks</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {list.map((r, i) => (
            <tr key={r.key} style={{ pageBreakInside: "avoid" }}>
              <td style={td}>{i + 1}</td>
              <td style={{ ...td, fontWeight: 600 }}>{r.custName}</td>
              <td style={td}>{r.area}</td>
              <td style={td}>
                {r.assignmentName}
                {r.isTemp ? " (Temp)" : ""}
              </td>
              <td style={td}>{r.shift}</td>
              {view === "new" ? (
                <>
                  <td style={td}>{shortDate(r.fromDate)}</td>
                  <td style={td}>{r.toDate ? shortDate(r.toDate) : "UFN"}</td>
                  <td style={num}>{r.filledDays}</td>
                </>
              ) : (
                <>
                  <td style={num}>{r.blankInWindow.length}</td>
                  <td style={td}>{r.blankLabel}</td>
                  <td style={{ ...td, borderBottom: "1px solid #999" }} />
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div style={{ ...base, padding: "6mm", background: "#fff", boxSizing: "border-box" }}>
      {header}
      {body}
    </div>
  );
};

export default DutyListPrintPreview;
